import { useCallback } from 'react';
import { query, where } from 'firebase/firestore';
import useManageResponderMapping from 'data/hooks/useManageResponderMapping';
import useHttpQuery from 'data/firestore/useHttpQuery';
import { Workflow } from 'pages/Skills/types';
import { ACTIONS_BASE_URL } from '../../../../../constants';
import { CellWrapper } from '../../../../Skills/columns/styled';
import useResponderMapping from '../../../../../data/firestore/agent/respondermapping/useCollection';
import ChannelDeliveryPicker, { ChannelDeliveryPickerValue } from '../ChannelDeliveryPicker';
import { getChannelFeatureName } from '../helpers/getChannelFeatureName';

type Automation = Array<{ id: string; name: string }>;
interface Props {
    row?: Record<string, unknown>;
    agentId?: string;
}

const ChannelDeliveryColumn = ({ row, agentId = '' }: Props) => {
    const channel = row?.id as string;
    const featureName = getChannelFeatureName(channel);

    const {
        data: automationsData,
    } = useHttpQuery<{ data: Workflow[] }>({
        pathname: `/workflows/request-response?agentId=${agentId}`,
        server: ACTIONS_BASE_URL,
        queryOptions: {
            enabled: Boolean(agentId),
        },
    });

    const automations: Automation = automationsData?.data || [];

    const {
        create: createMapping,
        update: updateMapping,
        delete: deleteMapping,
    } = useManageResponderMapping(agentId);

    const { data: responderMapping } = useResponderMapping(agentId, {
        listQueryFn: (ref) => query(ref, where('feature', '==', featureName)),
        disabled: !agentId || !channel,
        track: [agentId, featureName],
    });

    const [mapping] = responderMapping || [];
    const value: ChannelDeliveryPickerValue | undefined = (() => {
        if (mapping?.type === 'workflow') {
            return { type: 'workflow', id: mapping.workflowId };
        }
        if (mapping?.type === 'conversations-api') {
            return { type: 'conversations-api' };
        }
        return undefined;
    })();

    const updateChannelMappipng = useCallback(
        async (newChannelConfig: ChannelDeliveryPickerValue) => {
            const { type, id } = newChannelConfig;

            if (type === 'manychat-api') {
                if (mapping) await deleteMapping(mapping.id);
                return;
            }

            if (type === 'conversations-api') {
                if (!mapping) {
                    await createMapping({
                        type: 'conversations-api',
                        feature: featureName as string,
                    });
                } else {
                    await updateMapping({
                        type: 'conversations-api',
                        mappingId: mapping.id,
                    });
                }
                return;
            }

            if (type === 'workflow') {
                if (!mapping) {
                    await createMapping({
                        type: 'workflow',
                        feature: featureName as string,
                        targetId: id as string,
                    });
                } else {
                    await updateMapping({
                        type: 'workflow',
                        mappingId: mapping.id,
                        targetId: id as string,
                    });
                }
            }
        },
        [createMapping, deleteMapping, featureName, mapping, updateMapping],
    );

    return (
        <CellWrapper>
            <ChannelDeliveryPicker
                channel={channel}
                automations={automations}
                value={value}
                onChange={updateChannelMappipng}
            />
        </CellWrapper>
    );
};

export default ChannelDeliveryColumn;
